import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { PaymentMethodNote, PaymentMethodNoteResult } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const noteKeys = {
  list: (restaurantId: string) => ['payment-method-notes', restaurantId] as const,
};

export function usePaymentMethodNotes() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: noteKeys.list(restaurantId),
    queryFn: async () => {
      return unwrapResult<PaymentMethodNote[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/payment-method-notes', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

// Set or clear in one call — an empty note removes the badge.
export function useSetPaymentMethodNote() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ methodId, note }: { methodId: string; note: string }) =>
      unwrapResult<PaymentMethodNoteResult>(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/payment-method-notes/{methodId}', {
          params: { path: { restaurantId, methodId } },
          body: { note },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteKeys.list(restaurantId) });
    },
  });
}
